// The tab title and the GA4 page_viewed event for the SPA, the only writer of
// `document.title`.
//
// Subscribed at module scope (not in a component effect) so the router's
// initial resolution is captured too; a mount effect races it. `onResolved`
// fires once per committed navigation, after guards and redirect chains
// settle; `onBeforeLoad` would count destinations the user never reached
// (e.g. `_authed`'s login bounce). The GTM container must have Enhanced
// Measurement history-based page views DISABLED or every navigation
// double-counts.
//
// The title is written before the push: gtag stamps every event with the
// title it reads at firing time, so a title set after render would reach GA4
// one navigation late.
//
// A static page's title is the editor's search title when the cached list has
// one. The navigation never waits for the list: it is titled from the cache
// (the served seed on a first load, so no read there), or from the page's code
// subject when the list is missing, and retitled when a read lands. page_viewed
// carries the title the tab had when it fired; later events carry the new one.
// A save patches the cached list in place, so a revisit after one is titled
// with the saved copy at once.

import { hashKey, type QueryClient } from '@tanstack/react-query';
import type { AnyRouter } from '@tanstack/react-router';
import { isStaticPagePath } from '@leanin/shared/static-pages';
import { cachedStaticPageTitle, pageSeoKeys, refreshPageSeo } from '../queries/page-seo';
import { resolvePageMeta, type PageMeta, type PageMetaLoaderData } from './page-meta';

export interface PageViewsEnvironment {
  readonly router: AnyRouter;
  readonly queryClient: QueryClient;
  readonly doc: Document;
  readonly track: (meta: PageMeta) => void;
}

export function subscribePageViews({
  router,
  queryClient,
  doc,
  track,
}: PageViewsEnvironment): () => void {
  let lastTrackedHref: string | null = null;

  function metaFor(pathname: string): PageMeta {
    const leaf = router.state.matches.at(-1);
    const params = (leaf?.params ?? {}) as Record<string, string>;
    const loaderData = leaf?.loaderData as PageMetaLoaderData | undefined;
    const editorTitle = cachedStaticPageTitle(queryClient, pathname);
    return resolvePageMeta(
      pathname,
      params,
      editorTitle === null ? loaderData : { ...loaderData, pageTitle: editorTitle },
    );
  }

  const stopResolved = router.subscribe('onResolved', ({ toLocation }) => {
    const meta = metaFor(toLocation.pathname);
    doc.title = meta.page_title;
    if (toLocation.href !== lastTrackedHref) {
      lastTrackedHref = toLocation.href;
      track(meta);
    }
    if (isStaticPagePath(toLocation.pathname)) refreshPageSeo(queryClient);
  });

  const listHash = hashKey(pageSeoKeys.list().queryKey);
  const stopList = queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated' || event.action.type !== 'success') return;
    if (event.query.queryHash !== listHash) return;
    const pathname = router.state.resolvedLocation?.pathname;
    if (pathname === undefined || !isStaticPagePath(pathname)) return;
    doc.title = metaFor(pathname).page_title;
  });

  return () => {
    stopResolved();
    stopList();
  };
}
